import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Calendar, MessageSquare, FileText, Clock, Palmtree, Receipt,
  Building2, Shield, ArrowRight, ChevronRight, Check, Zap, Users, FolderKanban
} from 'lucide-react';

const FEATURES = [
  { icon: Calendar, title: 'Einsatzplanung', desc: 'Wochenkalender mit Projekt-Lanes. Mitarbeiter per Drag & Click einplanen.', color: 'text-blue-400 bg-blue-400/10' },
  { icon: Receipt, title: 'Finanzdokumente', desc: 'Angebote, Rechnungen, Gutschriften mit PDF-Export. Angebot direkt in Rechnung umwandeln.', color: 'text-emerald-400 bg-emerald-400/10' },
  { icon: MessageSquare, title: 'Echtzeit-Chat', desc: 'Projektbezogener Chat mit WebSocket. Nachrichten bleiben gespeichert.', color: 'text-violet-400 bg-violet-400/10' },
  { icon: FileText, title: 'Dokumentenverwaltung', desc: 'Ordnerstruktur mit Berechtigungen. Bis 300 MB pro Datei, 5 GB Speicher.', color: 'text-amber-400 bg-amber-400/10' },
  { icon: Clock, title: 'Zeiterfassung', desc: 'Arbeitszeiten erfassen, Pausen dokumentieren, projektbezogen auswerten.', color: 'text-rose-400 bg-rose-400/10' },
  { icon: Palmtree, title: 'Urlaubsverwaltung', desc: 'Anträge stellen, genehmigen, ablehnen. Übersicht für alle Mitarbeiter.', color: 'text-cyan-400 bg-cyan-400/10' },
];

const STEPS = [
  { num: '01', title: 'Registrieren', desc: 'Konto erstellen, Firmendaten hinterlegen, Logo hochladen.' },
  { num: '02', title: 'Team einladen', desc: 'Mitarbeiter anlegen und Rollen zuweisen. Sofort einsatzbereit.' },
  { num: '03', title: 'Loslegen', desc: 'Projekte anlegen, Einsätze planen, Rechnungen schreiben.' },
];

const PRICING = [
  { name: 'Starter', price: '14,99', period: 'mtl. zzgl. MwSt.', yearly: '143,90', features: ['Bis 2 Mitarbeiter', '1 GB Speicher', 'Alle Kernfunktionen', 'PDF mit Firmenlogo', 'E-Mail-Versand'], cta: 'Jetzt starten', highlight: false },
  { name: 'Pro', price: '59,90', period: 'mtl. zzgl. MwSt.', yearly: '575,04', features: ['Bis 10 Mitarbeiter', '5 GB Speicher', 'Alle Funktionen', 'Berechtigungssystem', 'Materialkatalog', 'Prioritäts-Support'], cta: 'Jetzt starten', highlight: true },
  { name: 'Enterprise', price: '89,90', period: 'mtl. zzgl. MwSt.', yearly: '863,04', features: ['Unbegrenzte Mitarbeiter', '50 GB Speicher', 'Alle Funktionen', 'API-Zugang', 'Dedicated Support'], cta: 'Jetzt starten', highlight: false },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goApp = () => navigate(user ? '/dashboard' : '/register');

  return (
    <div className="bg-slate-950 text-slate-200 min-h-screen" data-testid="landing-page">

      {/* ─── Navbar ─── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/60' : ''}`}
        data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-sm flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Mont<span className="text-amber-500">ARO</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Funktionen</a>
            <a href="#how" className="text-sm text-slate-400 hover:text-white transition-colors">So funktioniert's</a>
            <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Preise</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm" data-testid="nav-to-app">
                Zum Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-400 hover:text-white" data-testid="nav-login">Anmelden</Button>
                <Button onClick={() => navigate('/login')} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]" data-testid="nav-register">
                  Kostenlos testen
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.08),transparent_60%)]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-slate-800/50" />
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-sm text-amber-400 text-xs font-mono uppercase tracking-wider mb-8">
              <Zap className="w-3 h-3" />Handwerksbetrieb-Software
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              AUTOMATISIERT.<br />
              RECHNET AB.<br />
              <span className="text-amber-500">SCHAFFT ORDNUNG.</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-xl mb-10 leading-relaxed">
              MontARO ist die All-in-One Plattform für Handwerksbetriebe. Einsatzplanung, Rechnungen, Zeiterfassung, Dokumentenverwaltung — alles in einer App.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={goApp} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_20px_rgba(245,158,11,0.3)] text-base px-8 py-6 active:scale-95 transition-all" data-testid="hero-cta">
                Jetzt kostenlos starten <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-sm text-base px-8 py-6">
                Funktionen ansehen
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />14 Tage kostenlos</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />Keine Kreditkarte</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" />DSGVO-konform</span>
            </div>
          </div>

          {/* Hero visual - app preview mockup */}
          <div className="mt-16 relative">
            <div className="bg-slate-900/80 border border-slate-800/60 rounded-sm p-1 shadow-2xl shadow-black/40">
              <div className="bg-slate-900 rounded-sm overflow-hidden">
                <div className="h-8 bg-slate-950 flex items-center gap-2 px-4">
                  <div className="w-3 h-3 rounded-full bg-rose-500/60" /><div className="w-3 h-3 rounded-full bg-amber-500/60" /><div className="w-3 h-3 rounded-full bg-emerald-500/60" />
                  <span className="text-[10px] font-mono text-slate-600 ml-2">app.montaro.de</span>
                </div>
                <div className="grid grid-cols-[200px_1fr] h-[340px]">
                  <div className="bg-slate-900 border-r border-slate-800 p-3 space-y-2">
                    {['Dashboard', 'Einsatzplanung', 'Projekte', 'Mitarbeiter', 'Finanzen', 'Dokumente'].map((item, i) => (
                      <div key={item} className={`px-3 py-2 rounded-sm text-xs ${i === 0 ? 'bg-amber-500 text-slate-950 font-bold' : 'text-slate-500'}`}>{item}</div>
                    ))}
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-4 gap-2">
                      {[{ l: 'Projekte', v: '12', c: 'text-blue-400' }, { l: 'Mitarbeiter', v: '8', c: 'text-emerald-400' }, { l: 'Offen', v: '3', c: 'text-amber-400' }, { l: 'Umsatz', v: '47.8K', c: 'text-rose-400' }].map(k => (
                        <div key={k.l} className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-3">
                          <p className="text-[10px] text-slate-500">{k.l}</p>
                          <p className={`text-lg font-bold font-mono ${k.c}`}>{k.v}</p>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 flex-1">
                      <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-3">
                        <p className="text-[10px] text-slate-500 mb-2">Heutige Einsätze</p>
                        {['Müller — Dachsanierung', 'Schmidt — Rohrinstallation', 'Weber — Fassade'].map(t => (
                          <div key={t} className="text-[10px] text-slate-400 py-1 border-b border-slate-800/30">{t}</div>
                        ))}
                      </div>
                      <div className="bg-slate-950/50 border border-slate-800/40 rounded-sm p-3">
                        <p className="text-[10px] text-slate-500 mb-2">Offene Rechnungen</p>
                        {['RE-2026-0014 — 4.280 €', 'RE-2026-0013 — 8.737 €', 'RE-2026-0012 — 2.150 €'].map(t => (
                          <div key={t} className="text-[10px] text-slate-400 py-1 border-b border-slate-800/30 font-mono">{t}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-amber-500/5 blur-2xl" />
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-3 block">Funktionen</span>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              ALLES WAS IHR BETRIEB BRAUCHT
            </h2>
            <p className="text-slate-400 mt-3 max-w-lg mx-auto">Von der Einsatzplanung bis zur Rechnung. Eine Plattform, keine Insellösungen.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-6 hover:border-slate-700 transition-all group"
                data-testid={`feature-${i}`}>
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-4 ${f.color}`}>
                  <f.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section id="how" className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-3 block">In 3 Schritten</span>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              SO SCHNELL SIND SIE DIGITAL
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.num} className="relative" data-testid={`step-${i}`}>
                <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-8">
                  <span className="text-5xl font-black text-amber-500/20 font-mono absolute top-4 right-6">{s.num}</span>
                  <h3 className="text-xl font-bold text-white mb-3 mt-4">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
                {i < 2 && <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-700" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-3 block">Preise</span>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              TRANSPARENT UND FAIR
            </h2>
            <p className="text-slate-400 mt-3">Keine versteckten Kosten. Jederzeit kündbar.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((p, i) => (
              <div key={p.name} data-testid={`pricing-${i}`}
                className={`rounded-sm p-6 flex flex-col ${p.highlight
                  ? 'bg-amber-500/5 border-2 border-amber-500/40 relative'
                  : 'bg-slate-900/50 border border-slate-800/60'}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-slate-950 text-xs font-bold rounded-sm">
                    BELIEBT
                  </div>
                )}
                <h3 className="text-lg font-bold text-white">{p.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-black text-white font-mono" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{p.price}€</span>
                  <span className="text-sm text-slate-500 ml-2">{p.period}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-amber-500 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button onClick={goApp}
                  className={p.highlight
                    ? 'bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)] w-full'
                    : 'bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 rounded-sm w-full'}>
                  {p.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24 border-t border-slate-800/50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            BEREIT FÜR MEHR ORDNUNG?
          </h2>
          <p className="text-slate-400 mb-8 max-w-md mx-auto">Testen Sie MontARO 14 Tage kostenlos. Keine Kreditkarte nötig.</p>
          <Button onClick={goApp} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_20px_rgba(245,158,11,0.3)] text-base px-10 py-6 active:scale-95 transition-all" data-testid="final-cta">
            Kostenlos starten <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-slate-800/50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
                <Zap className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
              </div>
              <span className="text-lg font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Mont<span className="text-amber-500">ARO</span>
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-slate-300 transition-colors">Impressum</a>
              <a href="#" className="hover:text-slate-300 transition-colors">Datenschutz</a>
              <a href="#" className="hover:text-slate-300 transition-colors">AGB</a>
              <a href="#" className="hover:text-slate-300 transition-colors">Kontakt</a>
            </div>
            <p className="text-xs text-slate-600 font-mono">© 2026 MontARO. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
